import React from "react";
import axios from "axios";
import { Alert, Button, Card, Checkbox, Metric, ShieldTick, TileGroup, Zap } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "./BroadcastEditContainer";
import BroadcastSidebar from "./BroadcastSidebar";
import SegmentFilters from "./SegmentFilters";
import DataDrivenSegmentForm from "../Segments/DataDrivenSegmentForm";

import { toastError } from "../../../shared/toastHelper";

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 40,
    minHeight: 300,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  text: {
    margin: 0,
    color: "var(--sl-text-secondary)",
  },
  choices: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 24,
  },
  tiles: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    flex: "1 1 400px",
    maxWidth: 620,
  },
  summary: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    flex: "0 1 300px",
  },
  hygieneHeading: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  hint: {
    margin: "4px 0 0 28px",
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
  },
};

const RecipientsTab = () => {
  const {
    csrfToken,
    team,
    venueOwnership,
    setTab,
    broadcast,
    updateBroadcast,
    scheduledSend,
    segmentFilters,
    visibleSegmentFilters,
    fetchRecipientCount,
    recipientsCount,
    segment,
    setSegment,
    fanFilterGroupId,
    setFanFilterGroupId,
    isEmailTemplate,
  } = useBroadcastEditContext();

  const saveDisabled = () => {
    if (broadcast.recipient_type === null) {
      return true;
    } else if (broadcast.segment_type === "existing") {
      return !segmentFilters[0]?.set;
    } else if (broadcast.segment_type === "new") {
      return !fanFilterGroupId || !segment.name;
    }
  };

  const handleNext = (e) => {
    e.preventDefault();

    if (broadcast.segment_type === "new" && segment.id) {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.put(`/teams/${team.id}/segments/${segment.id}`, {
        segment: {
          name: segment.name,
        }
      })
        .then(() => {
          if(!!scheduledSend) {
            isEmailTemplate ? window.location.search = "tab=details" : setTab("twilio-editor");
          } else {
            setTab(isEmailTemplate ? "from" : "twilio-content");
          }
        })
        .catch((error) => {
          const errors = error.response.data;
          toastError(errors.join(", "));
        });
    } else if(!!scheduledSend) {
      isEmailTemplate ? window.location.search = "tab=details" : setTab("twilio-editor");
    } else {
      setTab(isEmailTemplate ? "from" : "twilio-content");
    }
  };

  const setRecipientsToAllFans = () => {
    setSegment({});
    setFanFilterGroupId(null);

    updateBroadcast({
      recipient_type: "all_fans",
    }, false);
  };

  const setRecipientsToConditional = () => {
    if (!broadcast.segment?.id) {
      updateBroadcast({
        segment_type: "new",
        recipient_type: "conditional",
        broadcast_segment_attributes: {
          broadcast_id: broadcast.id,
          segment_attributes: {
            name: `${broadcast.name} - Custom Segment`,
            type: "Segments::DataDriven",
            team_id: team.id,
            fan_filter_group_attributes: {
              match_condition: "All",
              team_id: team.id,
            }
          }
        }
      }, false);
    } else {
      updateBroadcast({
        segment_type: "new",
        recipient_type: "conditional",
      }, false);
    }
  };

  const setSegmentTypeToExisting = () => {
    updateBroadcast({
      segment_type: "existing"
    }, false);
  };

  const setSegmentTypeToNew = () => {
    if (!broadcast.segment?.id) {
      updateBroadcast({
        broadcast: {
          segment_type: "new",
          broadcast_segment_attributes: {
            broadcast_id: broadcast.id,
            segment_attributes: {
              name: `${broadcast.name} - Custom Segment`,
              type: "Segments::DataDriven",
              team_id: team.id,
              fan_filter_group_attributes: {
                match_condition: "All",
                team_id: team.id,
              }
            }
          }
        }
      }, false);
    } else {
      updateBroadcast({
        broadcast: {
          segment_type: "new",
        }
      }, false);
    }
  };

  return (
    <BroadcastSidebar>
      <div style={styles.page}>
        <div style={styles.section}>
          <div>
            <h3 style={styles.title}>Recipients</h3>
            <p style={styles.text}>Who are you sending this email to?</p>
          </div>
          <div style={styles.choices}>
            <div style={styles.tiles}>
              <TileGroup
                aria-label="Recipients"
                columns={2}
                value={broadcast.recipient_type}
                onChange={(value) => (
                  value === "all_fans" ? setRecipientsToAllFans() : setRecipientsToConditional()
                )}
                items={[
                  { value: "conditional", label: "Fans matching conditions" },
                  { value: "all_fans", label: "Every fan in my workspace" },
                ]}
              />
              {broadcast.recipient_type === "conditional" ? (
                <>
                  <p style={styles.text}>Target the fans that who should receive this broadcast.</p>
                  {broadcast.parent_segment_filter ? (
                    <TileGroup
                      aria-label="Segment"
                      columns={2}
                      value={broadcast.segment_type}
                      onChange={(value) => (
                        value === "existing" ? setSegmentTypeToExisting() : setSegmentTypeToNew()
                      )}
                      items={[
                        { value: "existing", label: "Choose an existing segment" },
                        { value: "new", label: "Create a new segment" },
                      ]}
                    />
                  ) : null}
                </>
              ) : null}
            </div>
            {recipientsCount !== "--" ? (
              <div style={styles.summary}>
                <Metric
                  label="Recipients"
                  value={recipientsCount}
                  footer={broadcast.recipient_type === "all_fans" ? (
                    `Total fans in the ${venueOwnership.name} workspace`
                  ) : (
                    `Total fans matching conditions in the ${venueOwnership.name} workspace`
                  )}
                />
                {broadcast.recipient_type === "all_fans" ? (
                  <Alert
                    color="warning"
                    icon={Zap}
                    title="Best Practices"
                    description="Sending to everyone in your database can damage your sending reputation and deliverability."
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          {isEmailTemplate ? (
            <div style={{ maxWidth: 540 }}>
              <Card>
                <div style={styles.hygieneHeading}>
                  <ShieldTick width={16} height={16} style={{ color: "var(--sl-fg-success)" }} />
                  Email List Hygiene
                </div>
                <p style={{ ...styles.hint, margin: "4px 0 16px" }}>
                  Improve deliverability by filtering out disengaged recipients
                </p>
                <Checkbox
                  label="Target engaged audience only"
                  checked={broadcast.target_engaged_audience || false}
                  onChange={(e) => {
                    updateBroadcast({ target_engaged_audience: e.target.checked }, false);
                  }}
                />
                <p style={styles.hint}>
                  Exclude fans who were sent emails in the last 3 months but haven't opened any
                </p>
              </Card>
            </div>
          ) : null}
          {broadcast.recipient_type === "conditional" && broadcast.segment_type === "existing" ? (
            <SegmentFilters
              editable={true}
              showAddButton={visibleSegmentFilters.length === 0}
            />
          ) : broadcast.recipient_type === "conditional" && broadcast.segment_type === "new" && fanFilterGroupId ? (
            <DataDrivenSegmentForm
              csrfToken={csrfToken}
              team={team}
              segment={segment}
              setSegment={setSegment}
              fanFilterGroupId={fanFilterGroupId}
              setFanFilterGroupId={setFanFilterGroupId}
              showSegmentNameInput={true}
              onValidChangeCallback={fetchRecipientCount}
            />
          ) : null}
        </div>
        <div style={styles.footer}>
          <Button
            variant="primary"
            disabled={saveDisabled()}
            onClick={handleNext}
          >
            {!!scheduledSend ? "Save" : "Next"}
          </Button>
        </div>
      </div>
    </BroadcastSidebar>
  );
};

export default RecipientsTab;
